<script setup lang="ts">
/** Dialogue d'ajout d'une action de suivi (type, date, note, intervenants). */
import { computed, ref, watch } from 'vue'
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline'
import type {
  AccompActionActor,
  AccompActionInput,
  AccompActionType,
} from '@/v2/accompagnement/api/accompagnementTypes'
import {
  ACCOMP_ACTION_TYPE_OPTIONS,
  ACCOMP_ACTOR_KIND_OPTIONS,
} from '@/v2/accompagnement/constants/accompagnementConstants'
import { toDateInputValue } from '@/v2/accompagnement/helpers/accompagnementFormat'
import AccompActorSearch from '@/v2/accompagnement/components/AccompActorSearch.vue'

const props = defineProps<{ open: boolean; loading?: boolean }>()
const emit = defineEmits<{ close: []; confirmed: [input: AccompActionInput] }>()

const dialog = ref<HTMLDialogElement | null>(null)
const type = ref<AccompActionType>('POINT_CLIENT')
const date = ref('')
const note = ref('')
/** Temps passé en minutes — OBLIGATOIRE (≥ 1) : on trace l'effort de chaque action. */
const timeSpentMinutes = ref<number | null>(null)
const actors = ref<AccompActionActor[]>([])

function emptyActor(): AccompActionActor {
  return { kind: 'INTERNE', uuid: null, firstName: '', lastName: '', email: '', phone: '' }
}

function reset() {
  type.value = 'POINT_CLIENT'
  date.value = toDateInputValue(new Date().toISOString())
  note.value = ''
  timeSpentMinutes.value = null
  actors.value = [emptyActor()]
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      reset()
      if (!dialog.value?.open) dialog.value?.showModal()
    } else {
      dialog.value?.close()
    }
  },
)

function addActor() {
  actors.value.push(emptyActor())
}
function removeActor(i: number) {
  actors.value.splice(i, 1)
}

/** Vide les coordonnées (changement de type, ou « Changer »). */
function resetActor(a: AccompActionActor) {
  a.uuid = null
  a.firstName = ''
  a.lastName = ''
  a.email = ''
  a.phone = ''
}

function onActorSelected(
  i: number,
  pick: { uuid: string; firstName: string; lastName: string; email: string; phone: string },
) {
  const a = actors.value[i]
  a.uuid = pick.uuid
  a.firstName = pick.firstName
  a.lastName = pick.lastName
  a.email = pick.email
  a.phone = pick.phone
}

function actorIsFilled(a: AccompActionActor): boolean {
  return !!(a.firstName.trim() || a.lastName.trim() || a.email.trim() || a.phone.trim())
}

/** Temps valide = nombre ≥ 1 minute (champ obligatoire). */
const timeSpentValid = computed(() => {
  const t = timeSpentMinutes.value
  return typeof t === 'number' && Number.isFinite(t) && t >= 1
})

function confirm() {
  if (!type.value || !date.value || !timeSpentValid.value) return
  emit('confirmed', {
    type: type.value,
    date: date.value,
    note: note.value.trim(),
    timeSpentMinutes: Math.round(timeSpentMinutes.value as number),
    actors: actors.value.filter(actorIsFilled),
  })
}
</script>

<template>
  <dialog ref="dialog" class="modal" @close="emit('close')">
    <div class="modal-box max-w-2xl">
      <h3 class="text-lg font-semibold">Ajouter une action</h3>

      <div class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label for="action-type" class="mb-1 block text-sm font-medium text-base-content">
            Type d'action <span class="text-error" aria-hidden="true">*</span>
          </label>
          <select id="action-type" v-model="type" class="select select-bordered w-full">
            <option v-for="opt in ACCOMP_ACTION_TYPE_OPTIONS" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div>
          <label for="action-date" class="mb-1 block text-sm font-medium text-base-content">
            Date <span class="text-error" aria-hidden="true">*</span>
          </label>
          <input id="action-date" v-model="date" type="date" class="input input-bordered w-full" />
        </div>
      </div>

      <div class="mt-4">
        <label for="action-time" class="mb-1 block text-sm font-medium text-base-content">
          Temps passé <span class="text-error" aria-hidden="true">*</span>
          <span class="font-normal text-base-content/70">(minutes)</span>
        </label>
        <input
          id="action-time"
          v-model.number="timeSpentMinutes"
          type="number"
          min="1"
          step="15"
          inputmode="numeric"
          required
          aria-describedby="action-time-help"
          class="input input-bordered w-40 text-base"
          placeholder="ex. 45"
        />
        <p id="action-time-help" class="mt-1 text-sm text-base-content/70">
          Au moins 1 minute — le temps passé est tracé pour chaque action.
        </p>
      </div>

      <div class="mt-4">
        <label for="action-note" class="mb-1 block text-sm font-medium text-base-content">Note</label>
        <textarea
          id="action-note"
          v-model="note"
          rows="2"
          class="textarea textarea-bordered w-full text-base"
          placeholder="Ce qui a été fait, conclusions, prochaines étapes…"
        ></textarea>
      </div>

      <!-- Intervenants -->
      <fieldset class="mt-4">
        <legend class="mb-2 text-sm font-medium text-base-content">Intervenants</legend>
        <div class="space-y-3">
          <div v-for="(a, i) in actors" :key="i" class="space-y-2 rounded-lg border border-base-300 p-3">
            <div class="flex items-center gap-2">
              <select
                v-model="a.kind"
                class="select select-bordered select-sm flex-1"
                aria-label="Type d'intervenant"
                @change="resetActor(a)"
              >
                <option v-for="opt in ACCOMP_ACTOR_KIND_OPTIONS" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
              <button type="button" class="btn btn-ghost btn-xs gap-1 text-error" @click="removeActor(i)">
                <TrashIcon class="h-4 w-4" aria-hidden="true" />
                <span class="hidden sm:inline">Retirer</span>
              </button>
            </div>

            <!-- Interne / Contact : recherche d'un enregistrement existant -->
            <template v-if="a.kind === 'INTERNE' || a.kind === 'CONTACT'">
              <div
                v-if="a.firstName || a.lastName || a.email"
                class="flex items-center justify-between gap-2 rounded-md bg-base-200/60 px-3 py-2"
              >
                <div class="min-w-0">
                  <p class="truncate font-medium text-base-content">
                    {{ `${a.firstName} ${a.lastName}`.trim() || a.email }}
                  </p>
                  <p v-if="a.email" class="truncate text-sm text-base-content/70">{{ a.email }}</p>
                </div>
                <button type="button" class="btn btn-ghost btn-xs" @click="resetActor(a)">Changer</button>
              </div>
              <AccompActorSearch v-else :kind="a.kind" @select="onActorSelected(i, $event)" />
            </template>

            <!-- Externe : saisie libre -->
            <div v-else class="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <input v-model="a.firstName" type="text" class="input input-bordered input-sm" placeholder="Prénom" />
              <input v-model="a.lastName" type="text" class="input input-bordered input-sm" placeholder="Nom" />
              <input v-model="a.email" type="email" class="input input-bordered input-sm" placeholder="Email" />
              <input v-model="a.phone" type="tel" class="input input-bordered input-sm" placeholder="Téléphone" />
            </div>
          </div>
        </div>
        <button type="button" class="btn btn-ghost btn-sm mt-2 gap-1" @click="addActor">
          <PlusIcon class="h-4 w-4" aria-hidden="true" /> Ajouter un intervenant
        </button>
      </fieldset>

      <div class="modal-action">
        <button type="button" class="btn btn-ghost" :disabled="loading" @click="emit('close')">Annuler</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!type || !date || !timeSpentValid || loading"
          :aria-busy="loading"
          @click="confirm"
        >
          <span v-if="loading" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          Ajouter
        </button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="emit('close')">Fermer</button>
    </form>
  </dialog>
</template>
